// src/aircraft/FlightRow.tsx — shared flight-row component.
//
// Extracted from TailLookupSheet so it can be reused in both
// the tail-lookup bottom sheet and the AircraftPage Recent Flights section.
//
// Renders a single flight leg (origin → destination, date/time, type, distance)
// as a tappable row. Caller handles selection state + track loading.

import { MapPin, ChevronRight, Map, Loader2 } from '../shell/Icon';
import type { TailFlight } from '../../api/flightaware/tail-lookup.js';
import { formatLocalTimeBare } from '../lib/timeFormat.js';

// ── helpers ────────────────────────────────────────────────────────────────

export function formatFlightDate(iso: string | null | undefined): string {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '\u2014';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatFlightTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return formatLocalTimeBare(d);
}

export function truncateRoute(route: string | null | undefined, maxLen = 60): string {
  if (!route) return '';
  if (route.length <= maxLen) return route;
  return route.slice(0, maxLen - 1) + '\u2026';
}

// ── component ──────────────────────────────────────────────────────────────

export interface FlightRowProps {
  flight: TailFlight;
  isSelected: boolean;
  isLoadingTrack: boolean;
  onSelect: () => void;
}

export function FlightRow({ flight, isSelected, isLoadingTrack, onSelect }: FlightRowProps) {
  const dep = flight.origin.code || '\u2014';
  const arr = flight.destination.code || '\u2014';
  const date = formatFlightDate(flight.actualOff ?? flight.scheduledOff);
  const time = formatFlightTime(flight.actualOff ?? flight.scheduledOff);
  const type = flight.aircraftType ?? '';
  const dist = flight.routeDistance ?? '';
  const route = truncateRoute(flight.route);

  return (
    <button
      className={[
        'w-full text-left px-4 py-3 border-b border-cockpit-700 last:border-b-0',
        'flex items-center gap-3 transition-colors',
        isSelected
          ? 'bg-cockpit-700/70'
          : 'hover:bg-cockpit-800/70 active:bg-cockpit-700/50',
      ].join(' ')}
      onClick={onSelect}
      aria-label={`${dep} to ${arr} on ${date}`}
    >
      {/* Date + time block */}
      <div className="flex-shrink-0 w-16 text-center">
        <div className="text-[11px] font-semibold text-cockpit-100 leading-tight">{date}</div>
        {time && <div className="text-[10px] text-cockpit-400 mt-0.5">{time}</div>}
      </div>

      {/* Route block */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-cockpit-50">{dep}</span>
          <ChevronRight size={12} className="text-cockpit-500 flex-shrink-0" />
          <span className="text-sm font-semibold text-cockpit-50">{arr}</span>
          {type && (
            <span className="ml-1 text-[10px] font-mono bg-cockpit-700 text-cockpit-300 px-1.5 py-0.5 rounded flex-shrink-0">
              {type}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {dist && (
            <span className="text-[10px] text-cockpit-400 flex items-center gap-0.5">
              <MapPin size={9} />
              {dist}
            </span>
          )}
          {flight.filedAltitude && (
            <span className="text-[10px] text-cockpit-400">
              {flight.filedAltitude * 100} ft
            </span>
          )}
        </div>
        {route && (
          <div className="text-[10px] text-cockpit-500 mt-0.5 truncate">{route}</div>
        )}
      </div>

      {/* Track indicator */}
      <div className="flex-shrink-0">
        {isLoadingTrack ? (
          <Loader2 size={14} className="text-cockpit-400 animate-spin" />
        ) : isSelected ? (
          <Map size={14} className="text-sky-400" />
        ) : (
          <ChevronRight size={14} className="text-cockpit-600" />
        )}
      </div>
    </button>
  );
}
